import {
  Box,
  ClickAwayListener,
  Fade,
  FormControl,
  FormControlLabel,
  Paper,
  Popper,
  PopperPlacementType,
  Radio,
  RadioGroup,
  Tooltip,
} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { Alert } from '@material-ui/lab';
import { useGetAssignmentConfigQuery } from '@src/reducers/assignmentManager';
import { contactAssignmentActions } from '@src/reducers/contactAssignment';
import {
  contactAssignedSelector,
  isAssignedContactSuccessfullySelector,
  isAssignedContactUnsuccessfullySelector,
} from '@src/selectors/contactAssignment';
import { selectAuthenticationInfo } from '@src/selectors/user';
import {
  ContactAssignmentOptions,
  ContactAssignmentType,
} from '@src/types/contactAssignment';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import AssignContactToAgent from './AssignContactToAgent';
import AssignContactToDepartment from './AssignContactToDepartment';
import './ContactAssignmentPopper.styles.css';

const ContactAssignmentPopper: React.FC<{
  contactId: string;
  readOnly?: boolean;
  blockOpenPopper?: boolean;
  fetchAssignmentOnOpen?: boolean;
  placement?: PopperPlacementType;
  children: React.ReactNode;
  popperStyle?: React.CSSProperties;
}> = ({
  contactId,
  readOnly = false,
  placement = 'bottom-end',
  blockOpenPopper = false,
  fetchAssignmentOnOpen = false,
  children,
  popperStyle,
}) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeAssignmentType, setActiveAssignmentType] = useState<string>();

  const authUser = useSelector(selectAuthenticationInfo);
  const assignedToContact = useSelector(contactAssignedSelector(contactId));
  const isAssignedSuccesfully = useSelector(
    isAssignedContactSuccessfullySelector(contactId)
  );
  const isAssignedUnsuccesfully = useSelector(
    isAssignedContactUnsuccessfullySelector(contactId)
  );

  const assignmentConfig = useGetAssignmentConfigQuery();
  const assignmentPermission = assignmentConfig.data?.assignmentPermission;

  const readOnlyAssignment = useMemo(
    () =>
      readOnly ||
      !(
        assignmentPermission === 'ASSIGN_ANY_AGENT_OR_DEPARTMENT' ||
        authUser.admin
      ),
    [assignmentPermission, authUser.admin, readOnly]
  );

  const isOpenPopper = useMemo(
    () => Boolean(anchorEl) && !blockOpenPopper,
    [anchorEl, blockOpenPopper]
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  useEffect(() => {
    if (contactId && !fetchAssignmentOnOpen)
      dispatch(contactAssignmentActions.getAssignedToContact(contactId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId, fetchAssignmentOnOpen]);

  useEffect(() => {
    if (contactId && isOpenPopper && fetchAssignmentOnOpen)
      dispatch(contactAssignmentActions.getAssignedToContact(contactId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId, isOpenPopper, fetchAssignmentOnOpen]);

  const onChangeAssignmentType = (assignmentType: ContactAssignmentType) => {
    if (!readOnlyAssignment) setActiveAssignmentType(assignmentType);
  };

  useEffect(() => {
    if (assignedToContact)
      setActiveAssignmentType(
        assignedToContact.type || ContactAssignmentOptions.UNASIGNED
      );
  }, [assignedToContact]);

  useEffect(() => {
    if (
      !readOnlyAssignment &&
      activeAssignmentType === ContactAssignmentOptions.UNASIGNED &&
      assignedToContact?.type !== ContactAssignmentOptions.UNASIGNED &&
      !!assignedToContact?.type
    )
      dispatch(contactAssignmentActions.unassignToContact(contactId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAssignmentType]);

  return (
    <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
      <div>
        <div
          onClick={handleClick}
          onKeyUp={() => undefined}
          role="button"
          tabIndex={-1}
        >
          {children}
        </div>
        <Popper
          open={isOpenPopper}
          anchorEl={anchorEl}
          transition
          placement={placement}
          style={{ zIndex: 11, maxWidth: 260, ...popperStyle }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <Box
                p={1}
                minWidth={280}
                component={Paper}
                className="contact-assignment-popper"
              >
                <div className="contact-assignment-info">
                  <Tooltip
                    placement="top-end"
                    title={
                      <div style={{ maxWidth: 320 }}>
                        {formatMessage({ id: 'ContactAssigment.tooltipInfo' })}
                      </div>
                    }
                  >
                    <InfoOutlinedIcon />
                  </Tooltip>
                </div>
                <div className="contact-assignment-form">
                  <FormControl component="fieldset" fullWidth size="small">
                    <RadioGroup
                      name="assignedType"
                      value={activeAssignmentType}
                      onChange={(_, value) =>
                        onChangeAssignmentType(value as ContactAssignmentType)
                      }
                    >
                      <FormControlLabel
                        label={formatMessage({
                          id: 'ContactAssigment.assignedType.department',
                        })}
                        control={
                          <Radio
                            size="small"
                            color="primary"
                            checked={
                              ContactAssignmentOptions.DEPARTMENT ===
                              activeAssignmentType
                            }
                          />
                        }
                        value={ContactAssignmentOptions.DEPARTMENT}
                      />
                      {activeAssignmentType ===
                        ContactAssignmentOptions.DEPARTMENT && (
                        <Box px={2} pb={1}>
                          <AssignContactToDepartment
                            contactId={contactId}
                            readOnly={readOnlyAssignment}
                          />
                        </Box>
                      )}
                      <FormControlLabel
                        label={formatMessage({
                          id: 'ContactAssigment.assignedType.agent',
                        })}
                        control={
                          <Radio
                            size="small"
                            color="primary"
                            checked={
                              ContactAssignmentOptions.AGENT ===
                              activeAssignmentType
                            }
                          />
                        }
                        value={ContactAssignmentOptions.AGENT}
                      />
                      {activeAssignmentType ===
                        ContactAssignmentOptions.AGENT && (
                        <Box px={2} pb={1}>
                          <AssignContactToAgent
                            contactId={contactId}
                            readOnly={readOnlyAssignment}
                          />
                        </Box>
                      )}
                      <FormControlLabel
                        label={formatMessage({
                          id: 'ContactAssigment.assignedType.unassign',
                        })}
                        control={
                          <Radio
                            size="small"
                            color="primary"
                            checked={
                              ContactAssignmentOptions.UNASIGNED ===
                              activeAssignmentType
                            }
                          />
                        }
                        value={ContactAssignmentOptions.UNASIGNED}
                      />
                    </RadioGroup>
                  </FormControl>

                  {isAssignedSuccesfully && (
                    <Alert
                      variant="standard"
                      severity="success"
                      className="status-request-msg"
                    >
                      {formatMessage({
                        id: 'ContactAssigment.assignedSuccessfully',
                      })}
                    </Alert>
                  )}
                  {isAssignedUnsuccesfully && (
                    <Alert
                      variant="standard"
                      severity="error"
                      className="status-request-msg"
                    >
                      {formatMessage({
                        id: 'ContactAssigment.assignedUnsuccessfully',
                      })}
                    </Alert>
                  )}
                </div>
              </Box>
            </Fade>
          )}
        </Popper>
      </div>
    </ClickAwayListener>
  );
};

export default ContactAssignmentPopper;
